import { Injectable } from '@angular/core';
import { environment } from "../../../environments/non-vendor/environment";

import { AngularFireRemoteConfig } from '@angular/fire/remote-config';
import { IData } from 'src/app/models/interfaces/idata';
import { vendorConfig } from '../../../environments/vendor.config';
import { UtilityService } from '../utility.service';
import IVendorConfig from 'src/environments/Ivendor-config';
import { bluumMapper } from 'src/app/mappers/bluum-mapper';
import { cdwMapper } from 'src/app/mappers/cdw-mapper';
import { connectionMapper } from 'src/app/mappers/connection-mapper';
import { insightFrMapper, insightMapper } from 'src/app/mappers/insight-mapper';
import { synnexMapper } from 'src/app/mappers/synnex-mapper';
import { synnexAusMapper } from 'src/app/mappers/synnex-aus-mapper';
import { traferaMapper} from "src/app/mappers/trafera-mapper";

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private _config: IVendorConfig = vendorConfig;

  constructor(private remoteConfig: AngularFireRemoteConfig, private utilityService: UtilityService) { }

  async setup(): Promise<void> {
    let configName = environment.firebaseConfigParam;
    const localeInfo = this.utilityService.getLocaleInfo();
    if (!localeInfo.isUnitedStatesStandardEnglishLocale || localeInfo.countryCode != "us") {
      configName = `${configName}_${localeInfo.localeName}`;
    }

    this.setBrandSettings(configName);

    if (!this._config) {
      try {
        this._config = vendorConfig;
        // add vendor specific configs here.
        this.setBrandSettings(configName);

      } catch (error) {
        // TODO: At some point in the firebase console we need to copy make a singular config and then route to the set brand settings function.
        await this.remoteConfig.fetchAndActivate();
        let configName = environment.firebaseConfigParam;
        const localeInfo = this.utilityService.getLocaleInfo();
        if (!localeInfo.isUnitedStatesStandardEnglishLocale || localeInfo.countryCode != "us") {
          configName = `${configName}_${localeInfo.localeName}`;
        }
        const remoteConfigValue = await this.remoteConfig.getValue(configName);
        const data = JSON.stringify(remoteConfigValue);
        this._config = JSON.parse(data);
        console.error(error);
      }
    }
  }

  /**
    * Takes the config name uses it to call the correct config.
    * @param - Name of the evnvironment and it's local combined.
    *
    * this will currently be an exaustive hard coded list.
    * may have to play with some ideas on how to improve this.
  */
  setBrandSettings(configName: string): void {
    switch (configName) {
      case "bluum_enterprise_config":
        this.setBluumConfig();
        break;
      case "cdw_enterprise_config":
        this.setCdwConfig();
        break;
      case "connection_enterprise_config":
        this.setConnectionConfig("default");
        break;
      case "connection_gov_enterprise_config":
        this.setConnectionConfig("gov");
        break;
      // currently dandh is the same as the default config;
      case "dandh_enterprise_config":
        break;
      case "insight_education_config":
        this.setInsightConfig("edu");
        break;
      case "insight_enterprise_config":
        this.setInsightConfig("default");
        break;
      case "insight_enterprise_config_fr_CA":
        this.setInsightConfig("fr_CA");
        break;
      case "synnex_enterprise_config":
        this.setSynnexConfig("default");
        break;
      case "synnex_public_enterprise_config":
        this.setSynnexConfig("public");
        break;
      case "synnex_aus_config":
        this.setSynnexAusConfig()
        break;
      case "synnex_aus_config_en_AU":
        this.setSynnexAusConfig()
        break;
      case "trafera_enterprise_config":
        this.setTraferaConfig();
        break;
      default:
        break;
    }
  }

  // This group of functions is for setting the custom configurations for each vendor.

  /**
    * This will apply bluum specific configurations.
  */
  setBluumConfig() {
    bluumMapper(this._config);
  }

  /**
    * This will apply Cdw specific configurations.
  */
  setCdwConfig() {
    cdwMapper(this._config);
  }

  /**
    * This will apply dandh specific configurations.
    * Note: currently the dandh config is the same as the default config if there are changes in the future create the function and the mapper.
  */

  /**
    * This will apply Connection specific configurations.
  */
  setConnectionConfig(use: string) {
    // Currently gov is the same;
    connectionMapper(this._config);
  }

  /**
    * This will apply insight specific configurations.
  */
  setInsightConfig(use: string) {
    switch (use) {
      case "fr_CA":
        this._config = insightFrMapper(this._config);
        break;
      default:
        insightMapper(this._config, use);
        break;
    }
  }

  /**
    * This will apply synnex specific configurations.
  */
  setSynnexConfig(use: string) {
    synnexMapper(this._config, use);
  }

  setSynnexAusConfig(){
    synnexAusMapper(this._config);
  }

  setTraferaConfig(){
    traferaMapper(this._config);
  }

  getString(fieldName: string): string {
    return this.getValue(fieldName) as string;
  }

  getNum(fieldName: string): number {
    return this.getValue(fieldName) as number;
  }

  getBool(fieldName: string): boolean {
    return this.getValue(fieldName) as boolean;
  }

  getStringArray(fieldName: string): string[] {
    return this.getValue(fieldName) as string[];
  }

  getValue(fieldName: any): any {
    const vendorConfigContent = vendorConfig;

    if (this._config) {
      // @ts-ignore
      return this._config[fieldName] ?? vendorConfigContent[fieldName];
    }

    // @ts-ignore
    return vendorConfigContent[fieldName];
  }

  /**
    *in the config there are several pairs of fields that are associated i.e. title & subtitle, brand data source name & display name
    * this function will find the related field given some initial info about the 2 fields
  */
  getRelatedFieldFromConfig(sourceFieldValue: string, sourceFieldIdentifier: string, relatedFieldIdentifier: string): string {
    let relatedFieldValue: string = "";

    const configContent = this._config ? this._config : vendorConfig;

    for (const property in configContent) {
      // @ts-ignore
      if (configContent[property] === sourceFieldValue) {
        const sourceFieldName = property;

        const relatedFieldName = sourceFieldName.replace(sourceFieldIdentifier, relatedFieldIdentifier);
        // @ts-ignore
        relatedFieldValue = configContent[relatedFieldName];
      }
    }

    return relatedFieldValue;
  }
}
